import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from '../index.js';
import { getAuthHeadersSync } from '../../shared/utils/authHelpers';

interface EntityAttribute {
  [key: string]: any;
}

interface TimelineEvent {
  timestamp: string;
  title: string;
  description: string;
  eventType: string;
}

interface EntityDetails {
  id: string;
  name: string;
  type: string;
  risk: number;
  description?: string;
  tags?: string[];
  attributes?: EntityAttribute;
  timeline?: TimelineEvent[];
}

interface Relationship {
  id: string;
  type: string;
  sourceId: string;
  targetId: string;
  targetType: string;
  targetName: string;
  weight: number;
  timestamp: string;
}

// New interfaces for entity management
interface Entity {
  id: string;
  name: string;
  entity_type: string;
  status: string;
  description?: string;
  external_id?: string;
  source_system?: string;
  confidence_score: number;
  risk_score?: number;
  criticality_level?: string;
  attributes: Record<string, unknown>;
  metadata: Record<string, unknown>;
  first_seen: string;
  last_seen: string;
  last_enriched?: string;
  enrichment_sources: string[];
  created_at: string;
  updated_at: string;
  tags: Array<{
    id: string;
    name: string;
    color: string;
  }>;
  business_contexts: Array<{
    id: string;
    name: string;
    context_type: string;
  }>;
}

interface BusinessContext {
  id: string;
  name: string;
  context_type: string;
  description?: string;
  parent_id?: string;
  hierarchy_path?: string;
  level: number;
  business_owner?: string;
  technical_owner?: string;
  criticality_level?: string;
  compliance_requirements: string[];
  attributes: Record<string, unknown>;
  metadata: Record<string, unknown>;
  is_active: boolean;
  effective_date?: string;
  expiration_date?: string;
  created_at: string;
  updated_at: string;
  entity_count: number;
}

interface EntitiesResponse {
  entities: Entity[];
  total: number;
  skip: number;
  limit: number;
  has_more: boolean;
}

interface BusinessContextsResponse {
  business_contexts: BusinessContext[];
  total: number;
  skip: number;
  limit: number;
  has_more: boolean;
}

interface EntityState {
  entities: Entity[];
  selectedEntity: Entity | null;
  selectedBusinessContext: BusinessContext | null;
  loading: boolean;
  error: string | null;
  details: Record<string, unknown>;
  loadingDetails: boolean;
  loadingRelationships: boolean;
  relationships: Record<string, unknown>;
  businessContexts: BusinessContext[];
  entitiesLoading: boolean;
  businessContextsLoading: boolean;
  entitiesError: string | null;
  businessContextsError: string | null;
  entitiesTotal: number;
  businessContextsTotal: number;
}

const initialState: EntityState = {
  entities: [],
  selectedEntity: null,
  selectedBusinessContext: null,
  loading: false,
  error: null,
  details: {},
  loadingDetails: false,
  loadingRelationships: false,
  relationships: {},
  businessContexts: [],
  entitiesLoading: false,
  businessContextsLoading: false,
  entitiesError: null,
  businessContextsError: null,
  entitiesTotal: 0,
  businessContextsTotal: 0,
};

// Async thunk for fetching entity details
export const fetchEntityDetails = createAsyncThunk(
  'entity/fetchEntityDetails',
  async (entityId: string, { rejectWithValue }) => {
    try {
      // Updated to support both /api/v1 and /v1 paths after BRAIN repository consolidation
      const response = await axios.get(`/api/alerts/entities/${entityId}`, {
        headers: getAuthHeadersSync()
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to fetch entity details'
      );
    }
  }
);

// Async thunk for fetching entity relationships
export const fetchEntityRelationships = createAsyncThunk(
  'entity/fetchEntityRelationships',
  async (entityId: string, { rejectWithValue }) => {
    try {
      // Updated to support both /api/v1 and /v1 paths after BRAIN repository consolidation
      const response = await axios.get(`/api/alerts/entities/${entityId}/relationships`, {
        headers: getAuthHeadersSync()
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to fetch entity relationships'
      );
    }
  }
);

// Async thunk for updating entity attributes
export const updateEntityAttributes = createAsyncThunk(
  'entity/updateEntityAttributes',
  async ({ entityId, attributes }: { entityId: string; attributes: EntityAttribute }, { rejectWithValue }) => {
    try {
      // Updated to support both /api/v1 and /v1 paths after BRAIN repository consolidation
      const response = await axios.patch(`/api/alerts/entities/${entityId}/attributes`, { attributes });
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as any)?.response?.data?.message || 'Failed to update entity attributes'
      );
    }
  }
);

// Async thunk for adding a tag to an entity
export const addEntityTag = createAsyncThunk(
  'entity/addEntityTag',
  async ({ entityId, tag }: { entityId: string; tag: string }, { rejectWithValue }) => {
    try {
      // Updated to support both /api/v1 and /v1 paths after BRAIN repository consolidation
      const response = await axios.post(`/api/alerts/entities/${entityId}/tags`, { tag }, {
        headers: getAuthHeadersSync()
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to add entity tag'
      );
    }
  }
);

// Async thunk for removing a tag from an entity
export const removeEntityTag = createAsyncThunk(
  'entity/removeEntityTag',
  async ({ entityId, tag }: { entityId: string; tag: string }, { rejectWithValue }) => {
    try {
      // Updated to support both /api/v1 and /v1 paths after BRAIN repository consolidation
      const response = await axios.delete(`/api/alerts/entities/${entityId}/tags/${tag}`);
      return { tag, ...response.data };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to remove entity tag'
      );
    }
  }
);

// ===== NEW ENTITY MANAGEMENT ASYNC THUNKS =====

// Async thunks for API calls
export const fetchEntities = createAsyncThunk(
  'entities/fetchEntities',
  async (params: { skip?: number; limit?: number; search?: string } = {}, { rejectWithValue }) => {
    const { skip = 0, limit = 50, search } = params;
    const queryParams = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      ...(search && { search })
    });
    
    try {
      // Updated to support both /api/v1 and /v1 paths after BRAIN repository consolidation
      const response = await axios.get(`/api/alerts/entities?${queryParams}`, {
        headers: getAuthHeadersSync()
      });
      return response.data as EntitiesResponse;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to fetch entities'
      );
    }
  }
);

// Fetch business contexts with filtering and pagination
export const fetchBusinessContexts = createAsyncThunk(
  'entity/fetchBusinessContexts',
  async (params: {
    skip?: number;
    limit?: number;
    context_type?: string;
    parent_id?: string;
    search?: string;
    is_active?: boolean;
  } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
      if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params.context_type) queryParams.append('context_type', params.context_type);
      if (params.parent_id) queryParams.append('parent_id', params.parent_id);
      if (params.search) queryParams.append('search', params.search);
      if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
      
      // Updated to support both /api/v1 and /v1 paths after BRAIN repository consolidation
      const response = await axios.get(`/api/alerts/business-contexts?${queryParams.toString()}`, {
        headers: getAuthHeadersSync()
      });
      return response.data as BusinessContextsResponse;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to fetch business contexts'
      );
    }
  }
);

// Create a new entity
export const createEntity = createAsyncThunk(
  'entity/createEntity',
  async (entityData: {
    name: string;
    entity_type: string;
    status?: string;
    description?: string;
    external_id?: string;
    source_system?: string;
    confidence_score?: number;
    risk_score?: number;
    criticality_level?: string;
    attributes?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
    enrichment_sources?: string[];
  }, { rejectWithValue }) => {
    try {
      // Updated to support both /api/v1 and /v1 paths after BRAIN repository consolidation
      const response = await axios.post('/api/alerts/entities', entityData, {
        headers: getAuthHeadersSync()
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to create entity'
      );
    }
  }
);

// Update an existing entity
export const updateEntity = createAsyncThunk(
  'entity/updateEntity',
  async ({ entityId, entityData }: {
    entityId: string;
    entityData: {
      name?: string;
      status?: string;
      description?: string;
      risk_score?: number;
      criticality_level?: string;
      attributes?: Record<string, unknown>;
      metadata?: Record<string, unknown>;
      enrichment_sources?: string[];
    };
  }, { rejectWithValue }) => {
    try {
      // Updated to support both /api/v1 and /v1 paths after BRAIN repository consolidation
      const response = await axios.put(`/api/alerts/entities/${entityId}`, entityData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to update entity attributes'
      );
    }
  }
);

// Delete (archive) an entity
export const deleteEntity = createAsyncThunk(
  'entity/deleteEntity',
  async (entityId: string, { rejectWithValue }) => {
    try {
      // Updated to support both /api/v1 and /v1 paths after BRAIN repository consolidation
      const response = await axios.delete(`/api/alerts/entities/${entityId}`);
      return { entityId, ...response.data };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to delete entity'
      );
    }
  }
);

// Create a new business context
export const createBusinessContext = createAsyncThunk(
  'entity/createBusinessContext',
  async (contextData: {
    name: string;
    context_type: string;
    description?: string;
    parent_id?: string;
    business_owner?: string;
    technical_owner?: string;
    criticality_level?: string;
    compliance_requirements?: string[];
    attributes?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
    is_active?: boolean;
    effective_date?: string;
    expiration_date?: string;
  }, { rejectWithValue }) => {
    try {
      // Updated to support both /api/v1 and /v1 paths after BRAIN repository consolidation
      const response = await axios.post('/api/alerts/business-contexts', contextData, {
        headers: getAuthHeadersSync()
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to create business context'
      );
    }
  }
);

const entitySlice = createSlice({
  name: 'entity',
  initialState,
  reducers: {
    clearEntityError: (state, _action) => {
      state.error = null;
    },
    resetEntityState: (state, _action) => {
      state.entities = [];
      state.selectedEntity = null;
      state.loading = false;
      state.error = null;
      state.details = {};
      state.loadingDetails = false;
      state.loadingRelationships = false;
      state.relationships = {};
      state.businessContexts = [];
      state.entitiesLoading = false;
      state.businessContextsLoading = false;
      state.entitiesError = null;
      state.businessContextsError = null;
      state.entitiesTotal = 0;
      state.businessContextsTotal = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchEntityDetails
      .addCase(fetchEntityDetails.pending, (state) => {
        state.loadingDetails = true;
        state.error = null;
      })
      .addCase(fetchEntityDetails.fulfilled, (state, action: PayloadAction<Record<string, unknown>>) => {
        state.loadingDetails = false;
        state.details = action.payload;
      })
      .addCase(fetchEntityDetails.rejected, (state, action) => {
        state.loadingDetails = false;
        state.error = action.payload as string;
      })
      
      // Handle fetchEntityRelationships
      .addCase(fetchEntityRelationships.pending, (state) => {
        state.loadingRelationships = true;
        state.error = null;
      })
      .addCase(fetchEntityRelationships.fulfilled, (state, action: PayloadAction<Record<string, unknown>>) => {
        state.loadingRelationships = false;
        state.relationships = action.payload;
      })
      .addCase(fetchEntityRelationships.rejected, (state, action) => {
        state.loadingRelationships = false;
        state.error = action.payload as string;
      })
      
      // Handle updateEntityAttributes
      .addCase(updateEntityAttributes.fulfilled, (state, action: PayloadAction<any>) => {
        if (state.details) {
          state.details = {
            ...state.details,
            attributes: {
              ...(state.details.attributes as Record<string, unknown> || {}),
              ...(action.payload?.attributes as Record<string, unknown> || {})
            }
          };
        }
      })
      
      // Handle addEntityTag
      .addCase(addEntityTag.fulfilled, (state, action: PayloadAction<any>) => {
        if (state.details) {
          const tags = Array.isArray(state.details.tags) ? state.details.tags : [];
          const newTag = action.payload?.tag;
          if (newTag && !tags.includes(newTag)) {
            state.details = {
              ...state.details,
              tags: [...tags, newTag]
            };
          }
        }
      })
      
      // Handle removeEntityTag
      .addCase(removeEntityTag.fulfilled, (state, action: PayloadAction<any>) => {
        if (state.details && Array.isArray(state.details.tags)) {
          const tagToRemove = action.payload?.tag;
          state.details = {
            ...state.details,
            tags: state.details.tags.filter(tag => tag !== tagToRemove)
          };
        }
      })
      
      // Handle fetchEntities
      .addCase(fetchEntities.pending, (state) => {
        state.entitiesLoading = true;
        state.entitiesError = null;
      })
      .addCase(fetchEntities.fulfilled, (state, action: PayloadAction<EntitiesResponse>) => {
        state.entitiesLoading = false;
        state.entities = action.payload.entities;
        state.entitiesTotal = action.payload.total;
      })
      .addCase(fetchEntities.rejected, (state, action) => {
        state.entitiesLoading = false;
        state.entitiesError = action.payload as string;
      })
      
      // Handle fetchBusinessContexts
      .addCase(fetchBusinessContexts.pending, (state) => {
        state.businessContextsLoading = true;
        state.businessContextsError = null;
      })
      .addCase(fetchBusinessContexts.fulfilled, (state, action: PayloadAction<BusinessContextsResponse>) => {
        state.businessContextsLoading = false;
        state.businessContexts = action.payload.business_contexts;
        state.businessContextsTotal = action.payload.total;
      })
      .addCase(fetchBusinessContexts.rejected, (state, action) => {
        state.businessContextsLoading = false;
        state.businessContextsError = action.payload as string;
      })
      
      // Handle createEntity
      .addCase(createEntity.pending, (state) => {
        state.entitiesLoading = true;
        state.entitiesError = null;
      })
      .addCase(createEntity.fulfilled, (state, action) => {
        state.entitiesLoading = false;
        // Optionally add the new entity to the list
        // state.entities.unshift(action.payload);
      })
      .addCase(createEntity.rejected, (state, action) => {
        state.entitiesLoading = false;
        state.entitiesError = action.payload as string;
      })
      
      // Handle updateEntity
      .addCase(updateEntity.pending, (state) => {
        state.entitiesLoading = true;
        state.entitiesError = null;
      })
      .addCase(updateEntity.fulfilled, (state, action) => {
        state.entitiesLoading = false;
        // Update the entity in the list if it exists
        const entityIndex = state.entities.findIndex(e => e.id === action.payload.id);
        if (entityIndex !== -1) {
          state.entities[entityIndex] = { ...state.entities[entityIndex], ...action.payload };
        }
        // Update selected entity if it matches
        if (state.selectedEntity && state.selectedEntity.id === action.payload.id) {
          state.details = { ...state.details, ...action.payload };
        }
      })
      .addCase(updateEntity.rejected, (state, action) => {
        state.entitiesLoading = false;
        state.entitiesError = action.payload as string;
      })
      
      // Handle deleteEntity
      .addCase(deleteEntity.pending, (state) => {
        state.entitiesLoading = true;
        state.entitiesError = null;
      })
      .addCase(deleteEntity.fulfilled, (state, action) => {
        state.entitiesLoading = false;
        // Remove or update the entity status in the list
        const entityIndex = state.entities.findIndex(e => e.id === action.payload.entityId);
        if (entityIndex !== -1) {
          state.entities[entityIndex].status = 'archived';
        }
        // Clear selected entity if it was deleted
        if (state.selectedEntity && state.selectedEntity.id === action.payload.entityId) {
          state.selectedEntity = null;
        }
      })
      .addCase(deleteEntity.rejected, (state, action) => {
        state.entitiesLoading = false;
        state.entitiesError = action.payload as string;
      })
      
      // Handle createBusinessContext
      .addCase(createBusinessContext.pending, (state) => {
        state.businessContextsLoading = true;
        state.businessContextsError = null;
      })
      .addCase(createBusinessContext.fulfilled, (state, action) => {
        state.businessContextsLoading = false;
        // Optionally add the new context to the list
        // state.businessContexts.unshift(action.payload);
      })
      .addCase(createBusinessContext.rejected, (state, action) => {
        state.businessContextsLoading = false;
        state.businessContextsError = action.payload as string;
      });
  }
});

export const { clearEntityError, resetEntityState } = entitySlice.actions;

// ===== SELECTORS =====

// Existing selectors
export const selectEntityDetails = (state: RootState) => state.entity.details;
export const selectEntityRelationships = (state: RootState) => state.entity.relationships;
export const selectEntityLoading = (state: RootState) => state.entity.loadingDetails;
export const selectEntityError = (state: RootState) => state.entity.error;

// New entity management selectors
export const selectEntitiesState = (state: RootState) => ({
  entities: state.entity.entities,
  loading: state.entity.entitiesLoading,
  error: state.entity.entitiesError,
  total: state.entity.entitiesTotal,
  selectedEntity: state.entity.selectedEntity
});

export const selectBusinessContextsState = (state: RootState) => ({
  businessContexts: state.entity.businessContexts,
  loading: state.entity.businessContextsLoading,
  error: state.entity.businessContextsError,
  total: state.entity.businessContextsTotal,
  selectedBusinessContext: state.entity.selectedBusinessContext
});

export const selectEntities = (state: RootState) => state.entity.entities;
export const selectBusinessContexts = (state: RootState) => state.entity.businessContexts;
export const selectEntitiesLoading = (state: RootState) => state.entity.entitiesLoading;
export const selectBusinessContextsLoading = (state: RootState) => state.entity.businessContextsLoading;
export const selectEntitiesError = (state: RootState) => state.entity.entitiesError;
export const selectBusinessContextsError = (state: RootState) => state.entity.businessContextsError;
export const selectSelectedEntity = (state: RootState) => state.entity.selectedEntity;
export const selectSelectedBusinessContext = (state: RootState) => state.entity.selectedBusinessContext;

export default entitySlice.reducer;
